"use client";

import { FC, useState } from "react";
import {
  Stack,
  HStack,
  Separator,
  Text,
  Link,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import { useQuery } from "@apollo/client/react";
import gql from "graphql-tag";
import { Price } from "../../../types/subscription";
import { openCheckout } from "../../../lib/paddleClient";
import { InfoIcon } from "lucide-react";

const GET_PRICE = gql`
  query GetPrice {
    price {
      id
      value
    }
  }
`;

type Props = {
  userId: string;
  email: string;
};

const SubscriptionCancelledMessage: FC<Props> = ({ userId, email }) => {
  const [hasRequestedDelete, setHasRequestedDelete] = useState(false);

  const { data: priceData } = useQuery<{ price: Price }>(GET_PRICE);
  const price = priceData?.price;

  const sendDeleteAccountEmail = async () => {
    await fetch("/api/account/send-delete-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });

    setHasRequestedDelete(true);
  };

  if (hasRequestedDelete) {
    return (
      <Stack gap={5}>
        <Text>
          An email has been sent to <b>{email}</b> from which you can delete
          your account.
        </Text>
        <Flex alignItems="center" gap={2}>
          <InfoIcon size={16} />
          <Text fontSize="sm">
            You may need to check your spam/junk folder.
          </Text>
        </Flex>
      </Stack>
    );
  }

  if (!price) {
    return (
      <Flex gap={3} alignItems="center">
        <Spinner size="sm" color="blue.solid" />
        <Text>Loading&hellip;</Text>
      </Flex>
    );
  }

  return (
    <>
      <Stack gap={1} mb={3}>
        <Text>Hi {email}!</Text>
        <Text fontSize="sm">
          Your subscription was cancelled. What would you like to do?
        </Text>
      </Stack>

      <Link
        as="button"
        variant="underline"
        textAlign="left"
        onClick={() => openCheckout(userId, email, price.id)}
      >
        <span>
          I would like to{" "}
          <Text as="span" fontWeight="bold">
            restore access
          </Text>{" "}
          to my account for {price.value}.
        </span>
      </Link>

      <HStack>
        <Separator flex="1" />
        <Text fontSize={14} flexShrink="0">
          OR
        </Text>
        <Separator flex="1" />
      </HStack>

      <Link
        as="button"
        variant="underline"
        textAlign="left"
        onClick={sendDeleteAccountEmail}
      >
        I would like to{" "}
        <Text as="span" fontWeight="bold">
          delete my account
        </Text>
        .
      </Link>
    </>
  );
};

export default SubscriptionCancelledMessage;
